import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    LucideAngularModule,
    Zap, CheckCircle, XCircle, Clock, Loader, ChevronDown, ChevronRight,
    Factory, ArrowLeft, RotateCcw, X, Eye, CircleDashed, Database, RefreshCw, AlertTriangle, Info, AlertOctagon,
    Settings, Save, ToggleLeft, ToggleRight, Layers
} from 'lucide-angular';
import {
    AlloraIntegrationService, AlloraRun, AlloraStep, RunStatus, StepStatus,
    AlloraImportError, AlloraJobTypeConfig, AlloraJobTypeConfigUpdatePayload,
    AlloraJobItem
} from '../../services/allora-integration.service';
import { AuthService } from '../../services/auth.service';

@Component({
    selector: 'app-allora-integration',
    templateUrl: './allora-integration.html',
    styleUrls: ['./allora-integration.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, LucideAngularModule],
})
export class AlloraIntegrationComponent {
    readonly service = inject(AlloraIntegrationService);
    readonly authService = inject(AuthService);

    readonly icons = {
        Zap, CheckCircle, XCircle, Clock, Loader, ChevronDown, ChevronRight,
        Factory, ArrowLeft, RotateCcw, X, Eye, CircleDashed, Database, RefreshCw, AlertTriangle, Info, AlertOctagon,
        Settings, Save, ToggleLeft, ToggleRight, Layers
    };

    // Expõe estados de carregamento da API
    readonly historyLoading     = this.service.historyLoading;
    readonly historyError       = this.service.historyError;
    readonly jobTypesLoading    = this.service.jobTypesLoading;
    readonly jobTypesError      = this.service.jobTypesError;
    readonly allJobTypesLoading = this.service.allJobTypesLoading;
    readonly allJobTypesError   = this.service.allJobTypesError;

    // ---------------- UI STATE ----------------
    /** id da run aberta em detalhe (null = tela de lista) */
    readonly detailRunId = signal<string | null>(null);
    /** rotas expandidas (sanfona) no detalhe */
    readonly expandedSteps = signal<Set<string>>(new Set());
    /** página atual por step (key → page index) */
    readonly stepPages = signal<Map<string, number>>(new Map());
    readonly DETAIL_PAGE_SIZE = 10;
    /** modal de nova integração */
    readonly showModal = signal(false);
    /** seleção do modal — chave = String(jobType) */
    readonly selectedJobTypes = signal<Set<string>>(new Set());
    /** estado da criação do sync (POST /api/AlloraSync) */
    readonly syncing   = signal(false);
    readonly syncError = signal<string | null>(null);
    /** uuid do job que está em retry agora */
    readonly retryingJobUuid = signal<string | null>(null);
    /** itens carregados por job uuid → lista de AlloraJobItem */
    readonly jobItemsMap     = signal<Map<string, AlloraJobItem[]>>(new Map());
    /** jobs com carregamento de itens em andamento */
    readonly jobItemsLoading = signal<Set<string>>(new Set());
    /** erro por job uuid */
    readonly jobItemsError   = signal<Map<string, string>>(new Map());
    /** página atual de itens por job uuid */
    readonly jobItemsPage    = signal<Map<string, number>>(new Map());
    readonly JOB_ITEMS_PAGE_SIZE = 10;
    /** toast global */
    readonly toast = signal<{ type: 'success' | 'error'; message: string } | null>(null);
    /** última vez que o histórico foi atualizado */
    readonly lastRefreshedAt = signal<Date | null>(null);

    // ---------------- VIEW STATE ----------------
    /** view ativa na tela principal */
    readonly activeView = signal<'history' | 'config'>('history');

    // ---------------- CONFIG STATE ----------------
    /** rascunhos editados pelo usuário (chave = String(jobType)) */
    readonly configDrafts = signal<Map<string, AlloraJobTypeConfigUpdatePayload>>(new Map());
    /** jobs com save em andamento */
    readonly savingJobTypes = signal<Set<string>>(new Set());

    /** Draft de um job type, inicializado a partir do valor da API */
    getDraft(jt: AlloraJobTypeConfig): AlloraJobTypeConfigUpdatePayload {
        const key = String(jt.jobType);
        return this.configDrafts().get(key) ?? {
            displayName: jt.displayName,
            category:    jt.category,
            batchSize:   jt.batchSize,
            enabled:     jt.isActive,
        };
    }

    isDirty(jt: AlloraJobTypeConfig): boolean {
        return this.configDrafts().has(String(jt.jobType));
    }

    isSaving(jt: AlloraJobTypeConfig): boolean {
        return this.savingJobTypes().has(String(jt.jobType));
    }

    patchDraft(jt: AlloraJobTypeConfig, patch: Partial<AlloraJobTypeConfigUpdatePayload>): void {
        const key     = String(jt.jobType);
        const current = this.getDraft(jt);
        this.configDrafts.update(m => new Map(m).set(key, { ...current, ...patch }));
    }

    saveConfig(jt: AlloraJobTypeConfig): void {
        const key     = String(jt.jobType);
        const payload = this.getDraft(jt);

        this.savingJobTypes.update(s => new Set(s).add(key));

        this.service.updateJobTypeConfig(jt.name, payload).subscribe({
            next: (updated) => {
                // Substitui pelo objeto retornado pela API (source of truth)
                const replaceFn = (list: AlloraJobTypeConfig[]) =>
                    list.map(j => String(j.jobType) === key ? updated : j);
                this.service.jobTypes.update(replaceFn);
                this.service.allJobTypes.update(replaceFn);
                this.configDrafts.update(m => { const n = new Map(m); n.delete(key); return n; });
                this.savingJobTypes.update(s => { const n = new Set(s); n.delete(key); return n; });
                this.showToast('success', `"${updated.displayName}" salvo com sucesso.`);
            },
            error: (err: any) => {
                this.savingJobTypes.update(s => { const n = new Set(s); n.delete(key); return n; });
                const msg = err?.error?.errors?.[0]?.messageCustomer
                    || err?.error?.message
                    || 'Falha ao salvar configuração.';
                this.showToast('error', msg);
            }
        });
    }

    discardDraft(jt: AlloraJobTypeConfig): void {
        this.configDrafts.update(m => { const n = new Map(m); n.delete(String(jt.jobType)); return n; });
    }

    readonly hasPendingChanges = computed(() => this.configDrafts().size > 0);

    /** Pode disparar nova integração e fazer retry (POST /AlloraSync, POST /Job/{uuid}/Retry) */
    readonly canTrigger = computed(() =>
        this.authService.hasRole(['Admin', 'Operator', 'AlloraSyncOperator']));

    /** Pode acessar a tela de configuração de job types (GET/PUT /JobTypeConfig — Admin only) */
    readonly canAdmin = computed(() =>
        this.authService.hasRole(['Admin']));

    /** Job types agrupados por categoria (para o modal de nova integração — apenas ativos) */
    readonly jobTypeGroups = computed(() => {
        const groups: { name: string; items: AlloraJobTypeConfig[] }[] = [];
        for (const jt of this.service.jobTypes()) {
            let g = groups.find(x => x.name === jt.category);
            if (!g) { g = { name: jt.category, items: [] }; groups.push(g); }
            g.items.push(jt);
        }
        return groups;
    });

    /** Job types agrupados por categoria (para a tela de config — todos, incluindo inativos) */
    readonly allJobTypeGroups = computed(() => {
        const groups: { name: string; items: AlloraJobTypeConfig[] }[] = [];
        for (const jt of this.service.allJobTypes()) {
            let g = groups.find(x => x.name === jt.category);
            if (!g) { g = { name: jt.category, items: [] }; groups.push(g); }
            g.items.push(jt);
        }
        return groups;
    });

    /** Flag para evitar re-seleção automática quando usuário desmarca itens */
    readonly #hasAutoSelected = signal(false);

    readonly #destroyRef = inject(DestroyRef);
    #pollingTimer: ReturnType<typeof setInterval> | null = null;

    /** Intervalo de polling em ms: 10s com run ativa, 30s caso contrário */
    readonly #POLL_RUNNING = 10_000;
    readonly #POLL_IDLE    = 30_000;

    constructor() {
        // loadHistory() já faz forkJoin com JobTypeConfig internamente
        this.service.loadHistory();
        this.lastRefreshedAt.set(new Date());

        // Auto-seleciona todos os job types quando carregam pela primeira vez com o modal aberto
        effect(() => {
            const types  = this.service.jobTypes();
            const isOpen = this.showModal();

            if (!isOpen) {
                this.#hasAutoSelected.set(false);
                return;
            }
            if (types.length > 0 && !this.#hasAutoSelected()) {
                this.selectedJobTypes.set(new Set(types.map(jt => String(jt.jobType))));
                this.#hasAutoSelected.set(true);
            }
        });

        // Carrega todos os job types (admin) ao entrar na view de config
        effect(() => {
            if (this.activeView() === 'config' && this.canAdmin() && this.service.allJobTypes().length === 0) {
                this.service.loadAllJobTypes();
            }
        });

        // Após salvar um config, também limpa o draft e recarrega allJobTypes
        // (já tratado em saveConfig via saveConfig → loadAllJobTypes não é necessário pois atualizamos o cache local)

        // Inicia polling adaptativo
        this.#scheduleNextPoll();
        this.#destroyRef.onDestroy(() => this.#stopPolling());
    }

    #scheduleNextPoll(): void {
        this.#stopPolling();
        const hasRunning = this.service.history().some(r => r.status === 'RUNNING');
        const delay = hasRunning ? this.#POLL_RUNNING : this.#POLL_IDLE;
        this.#pollingTimer = setInterval(() => this.#poll(), delay);
    }

    #poll(): void {
        if (this.service.historyLoading()) return;
        this.service.loadHistory();
        this.lastRefreshedAt.set(new Date());
        // Reagenda com o intervalo correto após cada resposta
        this.#scheduleNextPoll();
    }

    #stopPolling(): void {
        if (this.#pollingTimer) {
            clearInterval(this.#pollingTimer);
            this.#pollingTimer = null;
        }
    }

    refreshHistory(): void {
        this.service.loadHistory();
        this.lastRefreshedAt.set(new Date());
        this.#scheduleNextPoll();
    }

    // ---------------- LISTA (HOME) ----------------
    /** Lista exibida: execução ao vivo no topo + histórico */
    readonly runList = computed<AlloraRun[]>(() => {
        const live = this.service.currentRun();
        return live ? [live, ...this.service.history()] : this.service.history();
    });

    /** Indicadores: última integração com sucesso por entidade — baseado nos job types da API */
    readonly lastSuccessCards = computed(() =>
        this.service.jobTypes().map(jt => ({
            key:         String(jt.jobType),
            label:       jt.displayName,
            lastSuccess: this.#parseLastSuccess(jt.lastSuccessAt),
        }))
    );

    /** Converte lastSuccessAt da API em string | null.
     *  A API retorna "0001-01-01T…" quando nunca houve integração. */
    #parseLastSuccess(iso: string | null): string | null {
        if (!iso) return null;
        const d = new Date(iso);
        if (isNaN(d.getTime()) || d.getFullYear() < 2000) return null;
        return iso;
    }

    /** Transforma um ISO em texto relativo: "há 3 dias", "há 5 minutos", etc. */
    timeAgo(iso: string): string {
        const diffMs  = Date.now() - new Date(iso).getTime();
        const mins    = Math.floor(diffMs / 60_000);
        const hours   = Math.floor(diffMs / 3_600_000);
        const days    = Math.floor(diffMs / 86_400_000);

        if (mins  <  1)  return 'agora mesmo';
        if (mins  <  60) return `há ${mins} minuto${mins > 1 ? 's' : ''}`;
        if (hours <  24) return `há ${hours} hora${hours > 1 ? 's' : ''}`;
        if (days  <  30) return `há ${days} dia${days > 1 ? 's' : ''}`;

        const months = Math.floor(days / 30);
        if (months < 12) return `há ${months} ${months > 1 ? 'meses' : 'mês'}`;

        const years = Math.floor(days / 365);
        return `há ${years} ano${years > 1 ? 's' : ''}`;
    }

    // ---------------- DETALHE ----------------
    readonly detailRun = computed<AlloraRun | null>(() => {
        const id = this.detailRunId();
        if (!id) return null;
        const live = this.service.currentRun();
        if (live?.id === id) return live;
        return this.service.history().find(r => r.id === id) ?? null;
    });

    /** Rotas executadas (não puladas) da run em detalhe */
    readonly detailSteps = computed<AlloraStep[]>(() =>
        (this.detailRun()?.steps ?? []).filter(s => s.status !== 'SKIPPED'));

    // ---------------- HELPERS ----------------
    runProgress(run: AlloraRun): number {
        const steps = run.steps.filter(s => s.status !== 'SKIPPED');
        if (steps.length === 0) return 0;
        const done = steps.filter(s => s.status === 'SUCCESS' || s.status === 'ERROR').length;
        return Math.round((done / steps.length) * 100);
    }

    runFailures(run: AlloraRun): number {
        return run.steps.reduce((a, s) => a + s.totalFailures, 0);
    }

    runStepsSummary(run: AlloraRun): string {
        const steps = run.steps.filter(s => s.status !== 'SKIPPED');
        const done = steps.filter(s => s.status === 'SUCCESS' || s.status === 'ERROR').length;
        return `${done}/${steps.length} tarefas`;
    }

    stepPercent(step: AlloraStep): number {
        if (step.totalProcessed === 0) return step.status === 'SUCCESS' ? 100 : 0;
        return Math.round((step.totalSuccess / step.totalProcessed) * 100);
    }

    formatDuration(ms: number): string {
        if (!ms || ms <= 0) return '—';
        const totalSeconds = Math.floor(ms / 1000);
        const hours   = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (hours > 0 && minutes > 0) return `${hours}h ${minutes}min`;
        if (hours > 0)                return `${hours}h`;
        if (minutes > 0 && seconds > 0) return `${minutes}min ${seconds}s`;
        if (minutes > 0)              return `${minutes}min`;
        if (seconds > 0)              return `${seconds}s`;
        return `${ms}ms`;
    }

    runBadgeClass(status: RunStatus): string {
        switch (status) {
            case 'SUCCESS': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'ERROR': return 'bg-red-50 text-red-700 border-red-200';
            default: return 'bg-indigo-50 text-indigo-700 border-indigo-200';
        }
    }

    runStatusLabel(status: RunStatus): string {
        switch (status) {
            case 'SUCCESS': return 'Sucesso';
            case 'ERROR': return 'Erro';
            default: return 'Em execução';
        }
    }

    stepIcon(status: StepStatus) {
        switch (status) {
            case 'SUCCESS':     return this.icons.CheckCircle;
            case 'ERROR':       return this.icons.XCircle;
            case 'FETCH_ERROR': return this.icons.AlertTriangle;
            case 'RUNNING':     return this.icons.Loader;
            case 'SKIPPED':     return this.icons.CircleDashed;
            default:            return this.icons.Clock;
        }
    }

    stepIconClass(status: StepStatus): string {
        switch (status) {
            case 'SUCCESS':     return 'text-emerald-500';
            case 'ERROR':       return 'text-red-500';
            case 'FETCH_ERROR': return 'text-orange-500';
            case 'RUNNING':     return 'text-indigo-500 animate-spin';
            default:            return 'text-slate-300';
        }
    }

    // ---------------- AÇÕES: NAVEGAÇÃO ----------------
    openDetail(run: AlloraRun): void {
        this.detailRunId.set(run.id);
        this.expandedSteps.set(new Set());
        this.stepPages.set(new Map());
    }

    backToList(): void {
        this.detailRunId.set(null);
    }

    toggleStep(step: AlloraStep): void {
        const key = step.key;
        const wasExpanded = this.expandedSteps().has(key);
        this.expandedSteps.update(set => {
            const next = new Set(set);
            wasExpanded ? next.delete(key) : next.add(key);
            return next;
        });
        // Resetar página ao expandir/fechar
        this.stepPages.update(m => new Map(m).set(key, 0));

        // Ao expandir, carrega os itens do job via API (somente se tiver uuid)
        if (!wasExpanded && step.uuid) {
            this.#loadJobItems(step.uuid);
        }
    }

    isStepExpanded(key: string): boolean {
        return this.expandedSteps().has(key);
    }

    // ---------------- ITENS DO JOB ----------------

    #loadJobItems(jobUuid: string): void {
        // Não recarrega se já estiver em memória ou carregando
        if (this.jobItemsMap().has(jobUuid) || this.jobItemsLoading().has(jobUuid)) return;

        this.jobItemsLoading.update(s => new Set(s).add(jobUuid));
        this.jobItemsError.update(m => { const n = new Map(m); n.delete(jobUuid); return n; });

        this.service.getJobItems(jobUuid).subscribe({
            next: (items) => {
                this.jobItemsMap.update(m => new Map(m).set(jobUuid, items));
                this.jobItemsLoading.update(s => { const n = new Set(s); n.delete(jobUuid); return n; });
            },
            error: () => {
                this.jobItemsLoading.update(s => { const n = new Set(s); n.delete(jobUuid); return n; });
                this.jobItemsError.update(m => new Map(m).set(jobUuid, 'Falha ao carregar os itens do job.'));
            }
        });
    }

    isJobItemsLoading(jobUuid: string): boolean {
        return this.jobItemsLoading().has(jobUuid);
    }

    getJobItemsError(jobUuid: string): string | null {
        return this.jobItemsError().get(jobUuid) ?? null;
    }

    getJobItems(jobUuid: string): AlloraJobItem[] {
        return this.jobItemsMap().get(jobUuid) ?? [];
    }

    reloadJobItems(jobUuid: string, event: Event): void {
        event.stopPropagation();
        this.jobItemsMap.update(m => { const n = new Map(m); n.delete(jobUuid); return n; });
        this.#loadJobItems(jobUuid);
    }

    getJobItemsPage(jobUuid: string): number {
        return this.jobItemsPage().get(jobUuid) ?? 0;
    }

    getJobItemsTotalPages(jobUuid: string): number {
        const total = this.getJobItems(jobUuid).length;
        return Math.max(1, Math.ceil(total / this.JOB_ITEMS_PAGE_SIZE));
    }

    getPagedJobItems(jobUuid: string): AlloraJobItem[] {
        const page = this.getJobItemsPage(jobUuid);
        return this.getJobItems(jobUuid).slice(
            page * this.JOB_ITEMS_PAGE_SIZE,
            (page + 1) * this.JOB_ITEMS_PAGE_SIZE
        );
    }

    nextJobItemsPage(jobUuid: string, event: Event): void {
        event.stopPropagation();
        const page = this.getJobItemsPage(jobUuid);
        if (page < this.getJobItemsTotalPages(jobUuid) - 1) {
            this.jobItemsPage.update(m => new Map(m).set(jobUuid, page + 1));
        }
    }

    prevJobItemsPage(jobUuid: string, event: Event): void {
        event.stopPropagation();
        const page = this.getJobItemsPage(jobUuid);
        if (page > 0) {
            this.jobItemsPage.update(m => new Map(m).set(jobUuid, page - 1));
        }
    }

    trackJobItem(_: number, item: AlloraJobItem): string { return item.externalCode; }

    // ---------------- AÇÕES: MODAL / INTEGRAÇÃO ----------------
    openModal(): void {
        if (!this.canTrigger() || this.service.isRunning()) return;
        // Seleciona todos por padrão ao abrir; carrega job types da API
        this.service.loadJobTypes();
        this.selectedJobTypes.set(new Set(this.service.jobTypes().map(jt => String(jt.jobType))));
        this.showModal.set(true);
    }

    closeModal(): void {
        this.showModal.set(false);
        this.syncError.set(null);
    }

    toggleJobType(key: string): void {
        this.selectedJobTypes.update(set => {
            const next = new Set(set);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    }

    isJobTypeSelected(key: string): boolean {
        return this.selectedJobTypes().has(key);
    }

    toggleAllJobTypes(): void {
        const allKeys = this.service.jobTypes().map(jt => String(jt.jobType));
        this.selectedJobTypes.update(set =>
            set.size === allKeys.length ? new Set<string>() : new Set(allKeys));
    }

    confirmIntegration(): void {
        const keys     = this.selectedJobTypes();
        const selected = this.service.jobTypes().filter(jt => keys.has(String(jt.jobType)));
        if (selected.length === 0 || this.syncing()) return;

        const jobTypes = selected.map(jt => jt.jobType);

        this.syncing.set(true);
        this.syncError.set(null);

        this.service.createSync(jobTypes).subscribe({
            next: () => {
                this.syncing.set(false);
                this.showModal.set(false);
                // Recarrega a lista e retorna para a tela inicial
                this.detailRunId.set(null);
                this.service.loadHistory();
            },
            error: (err: any) => {
                this.syncing.set(false);
                const msg = err?.error?.message || err?.error?.title || err?.message
                    || 'Falha ao criar a integração. Tente novamente.';
                this.syncError.set(msg);
            }
        });
    }

    retry(step: AlloraStep, event: Event): void {
        event.stopPropagation();
        if (!this.canTrigger() || !step.uuid || this.retryingJobUuid()) return;

        this.retryingJobUuid.set(step.uuid);

        this.service.retryJob(step.uuid).subscribe({
            next: () => {
                this.retryingJobUuid.set(null);
                this.showToast('success', `Job "${step.label}" reenviado para reprocessamento.`);
                this.service.loadHistory();
            },
            error: (err: any) => {
                this.retryingJobUuid.set(null);
                const customerMsg = err?.error?.errors?.[0]?.messageCustomer
                    || err?.error?.message
                    || 'Falha ao reprocessar o job. Tente novamente.';
                this.showToast('error', customerMsg);
            }
        });
    }

    showToast(type: 'success' | 'error', message: string): void {
        this.toast.set({ type, message });
        setTimeout(() => this.toast.set(null), 4500);
    }

    // ---------------- PAGINAÇÃO DO DETALHE ----------------

    getStepPage(key: string): number {
        return this.stepPages().get(key) ?? 0;
    }

    getStepTotalPages(step: AlloraStep): number {
        return Math.max(1, Math.ceil(step.errors.length / this.DETAIL_PAGE_SIZE));
    }

    getPagedErrors(step: AlloraStep): AlloraImportError[] {
        const page = this.getStepPage(step.key);
        return step.errors.slice(page * this.DETAIL_PAGE_SIZE, (page + 1) * this.DETAIL_PAGE_SIZE);
    }

    nextStepPage(step: AlloraStep, event: Event): void {
        event.stopPropagation();
        const page = this.getStepPage(step.key);
        if (page < this.getStepTotalPages(step) - 1) {
            this.stepPages.update(m => new Map(m).set(step.key, page + 1));
        }
    }

    prevStepPage(key: string, event: Event): void {
        event.stopPropagation();
        const page = this.getStepPage(key);
        if (page > 0) {
            this.stepPages.update(m => new Map(m).set(key, page - 1));
        }
    }

    trackCard(_: number, card: { key: string; label: string; lastSuccess: string | null }): string { return card.key; }
    trackRun(_: number, run: AlloraRun): string { return run.id; }
    trackStep(_: number, step: AlloraStep): string { return step.key; }
    trackJobType(_: number, jt: AlloraJobTypeConfig): string { return String(jt.jobType); }
}
